import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Divider, IconButton, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import {
  MoreHoriz,
  Schedule,
  Check,
  BedtimeOutlined,
  SelfImprovement,
  AccessibilityNew,
  Air,
  DirectionsWalk,
  Spa,
  DeleteOutline,
  AddCircleOutline
} from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { CLEAR_ROUTINE_DAY, MY_WEEKLY_ROUTINE, SET_ROUTINE_DAY_INTENSITY } from 'api/graphql/play';
import { dayLabel, intensityLabel, muscleLabel, primaryMuscleOf, INTENSITY_VALUES } from 'components/play/constants';
import { estimateWorkout } from 'components/play/workoutEstimate';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import routes from 'common/routes';

// Cuántos ejercicios caben en la tarjeta antes de resumir el resto en "+N más".
const VISIBLE_EXERCISES = 4;

// Sugerencias de los días de descanso. Fijas por diseño (no hay contenido de recuperación en
// el backend). CUALQUIER día sin ejercicios es descanso, así que todos necesitan sugerencia:
// entre semana se propone movilidad (recuperación activa) y el fin de semana algo más pasivo.
const WEEKDAY_REST_TIPS = [
  { key: 'mobility', icon: AccessibilityNew },
  { key: 'stretching', icon: SelfImprovement },
  { key: 'breathing', icon: Air }
];

const WEEKEND_REST_TIPS = [
  { key: 'yoga', icon: Spa },
  { key: 'meditation', icon: SelfImprovement },
  { key: 'activeRest', icon: DirectionsWalk }
];

const REST_TIPS = {
  1: WEEKDAY_REST_TIPS,
  2: WEEKDAY_REST_TIPS,
  3: WEEKDAY_REST_TIPS,
  4: WEEKDAY_REST_TIPS,
  5: WEEKDAY_REST_TIPS,
  // Sábado (6) y domingo (7) son fin de semana: descanso más pasivo (yoga/meditación).
  6: WEEKEND_REST_TIPS,
  7: WEEKEND_REST_TIPS
};

const DEFAULT_REST_TIPS = WEEKEND_REST_TIPS;

/**
 * OTRIX Play — tarjeta de un día de la semana en el home.
 *
 * Muestra el músculo dominante del día, su intensidad, los ejercicios y la duración estimada.
 * El menú "···" permite fijar la intensidad a mano (el backend deja de recalcularla), ir a la
 * rutina completa o vaciar el día. Monocromo + dorado como acento (el mockup del cliente usaba
 * un color distinto por día; aquí el único acento es el día de hoy).
 */
function PlayDayCard({ day, items = [], intensity = null, isToday = false }) {
  const { t } = useTranslation(['play']);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [setIntensity] = useMutation(SET_ROUTINE_DAY_INTENSITY, { refetchQueries: [MY_WEEKLY_ROUTINE] });
  const [clearDay] = useMutation(CLEAR_ROUTINE_DAY, { refetchQueries: [MY_WEEKLY_ROUTINE] });

  // Todos los días son entrenables: un día SIN ejercicios es, simplemente, descanso. Agregar
  // ejercicios se hace desde el menú "···" (no hay un estado intermedio "entreno pendiente").
  const hasItems = items.length > 0;
  const isRest = !hasItems;
  const muscle = primaryMuscleOf(items);
  const estimate = estimateWorkout(items);

  const closeMenu = () => setAnchorEl(null);

  const notifyError = (message) => {
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `day-${day}-${Date.now()}`, variant: 'error', message })));
  };

  const handleIntensity = async (value) => {
    closeMenu();
    try {
      await setIntensity({ variables: { day_of_week: day, intensity: value } });
    } catch {
      notifyError(t('home.week.intensityError', { ns: 'play' }));
    }
  };

  const handleClear = async () => {
    try {
      await clearDay({ variables: { day_of_week: day } });
      setConfirmOpen(false);
    } catch {
      notifyError(t('routine.errorClear', { ns: 'play' }));
    }
  };

  const restTips = REST_TIPS[day] || DEFAULT_REST_TIPS;

  return (
    <>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          borderRadius: '16px',
          border: '1px solid',
          // "Hoy" ya no pinta el borde completo de dorado: borde neutro fuerte y el
          // acento queda en el label + dot (microacento).
          borderColor: isToday ? 'hairline.strong' : 'hairline.main',
          backgroundColor: hasItems ? 'surface.raised' : 'surface.overlay',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
          transition: 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1), border-color 0.22s, background-color 0.22s',
          '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' }
        }}>
        {/* Encabezado: día + menú */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5} sx={{ mb: 1 }}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                lineHeight: 1.2,
                fontWeight: 800,
                letterSpacing: '0.12em',
                color: isToday ? 'gold.main' : 'text.secondary'
              }}
              noWrap>
              {dayLabel(t, day)}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  flex: 'none',
                  backgroundColor: isRest ? 'text.secondary' : 'gold.main'
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {isRest ? t('home.week.rest', { ns: 'play' }) : t('home.week.train', { ns: 'play' })}
              </Typography>
            </Stack>
          </Stack>

          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label={t('home.week.options', { ns: 'play' })}
            sx={{ flex: 'none', color: 'text.secondary', '&:active': { transform: 'scale(0.98)' } }}>
            <MoreHoriz fontSize="small" />
          </IconButton>
        </Stack>

        {isRest && (
          <Stack spacing={1.5} alignItems="center" sx={{ flex: 1, justifyContent: 'center', py: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                // Sin esto el flex del Stack lo aplasta en mobile y la luna sale ovalada.
                flex: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'surface.raised'
              }}>
              <BedtimeOutlined fontSize="small" />
            </Box>
            <Stack spacing={0.25} alignItems="center">
              <Typography variant="body2" fontWeight={800}>
                {t('home.week.restTitle', { ns: 'play' })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('home.week.restBody', { ns: 'play' })}
              </Typography>
            </Stack>
            <Stack spacing={0.75} sx={{ width: '100%', pt: 0.5 }}>
              {restTips.map(({ key, icon: Icon }) => (
                <Stack key={key} direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <Icon sx={{ fontSize: 15, flex: 'none' }} />
                  <Typography variant="caption" noWrap>
                    {t(`home.week.restTips.${key}`, { ns: 'play' })}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {/* Acceso directo a las prácticas guiadas de recuperación (panel de descanso). */}
            <Typography
              role="button"
              tabIndex={0}
              onClick={() => navigate(routes.playWorkout.path)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(routes.playWorkout.path);
                }
              }}
              variant="caption"
              sx={{
                color: 'gold.main',
                fontWeight: 700,
                cursor: 'pointer',
                pt: 0.5,
                '&:hover': { textDecoration: 'underline' }
              }}>
              {t('home.week.restCta', { ns: 'play', defaultValue: 'Ver prácticas' })}
            </Typography>
          </Stack>
        )}

        {hasItems && (
          <>
            {/* Músculo + intensidad */}
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
              <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ flex: 1, minWidth: 0 }}>
                {muscleLabel(t, muscle)}
              </Typography>
              {intensity && (
                <Chip
                  size="small"
                  label={intensityLabel(t, intensity)}
                  sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                />
              )}
            </Stack>

            <Stack spacing={0.75} sx={{ flex: 1 }}>
              {items.slice(0, VISIBLE_EXERCISES).map((item) => (
                <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                    {item.exercise?.localized_name}
                  </Typography>
                  {item.target_reps && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
                      {item.target_sets ? `${item.target_sets}×` : ''}
                      {item.target_reps}
                    </Typography>
                  )}
                </Stack>
              ))}
              {items.length > VISIBLE_EXERCISES && (
                <Typography variant="caption" sx={{ color: 'gold.main', fontWeight: 700 }}>
                  {t('home.week.moreExercises', { ns: 'play', count: items.length - VISIBLE_EXERCISES })}
                </Typography>
              )}
            </Stack>

            <Divider sx={{ my: 1.25 }} />
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
              <Schedule sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('home.week.minutes', { ns: 'play', count: estimate.durationMin })}
              </Typography>
            </Stack>
          </>
        )}
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {/* Un día sin ejercicios no tiene intensidad que fijar. */}
        {hasItems && (
          <Typography
            variant="caption"
            sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
            {t('home.week.intensity', { ns: 'play' })}
          </Typography>
        )}
        {hasItems &&
          INTENSITY_VALUES.map((value) => (
            <MenuItem key={value} onClick={() => handleIntensity(value)} selected={value === intensity}>
              <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: value === intensity ? 700 : 500 }}>
                {intensityLabel(t, value)}
              </ListItemText>
              {value === intensity && <Check sx={{ fontSize: 16, ml: 1.5, color: 'selected.fg' }} />}
            </MenuItem>
          ))}
        {hasItems && <Divider />}
        {/* Todo día es entrenable: desde aquí se le agregan ejercicios (antes el único acceso
            era un CTA dentro de la card, que solo existía si el día venía marcado de entreno). */}
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(routes.playRoutine.path);
          }}>
          <AddCircleOutline fontSize="small" sx={{ mr: 1, color: 'gold.main' }} />
          <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'gold.main', fontWeight: 700 }}>
            {t('home.week.addExercises', { ns: 'play' })}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(routes.playRoutine.path);
          }}>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            {t('home.week.viewRoutine', { ns: 'play' })}
          </ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!hasItems}
          onClick={() => {
            closeMenu();
            setConfirmOpen(true);
          }}>
          <DeleteOutline fontSize="small" sx={{ mr: 1, color: 'sessionStatus.absent.fg' }} />
          <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'sessionStatus.absent.fg' }}>
            {t('home.week.clearDay', { ns: 'play' })}
          </ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={t('home.week.clearConfirmTitle', { ns: 'play', day: dayLabel(t, day) })}
        message={t('home.week.clearConfirmBody', { ns: 'play' })}
        confirmLabel={t('home.week.clearConfirmCta', { ns: 'play' })}
        onConfirm={handleClear}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

PlayDayCard.propTypes = {
  day: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.object),
  intensity: PropTypes.oneOf([...INTENSITY_VALUES, null]),
  isToday: PropTypes.bool
};

export default PlayDayCard;
